"use client";

import { useRouter } from "next/navigation";
import React, { ReactElement, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import FooterRenewalCoverLevel from "@/app/(pages)/[[...pathname]]/renewal/cover-level/footer-renewal-cover-level";
import HelpOverlayButton from "@/app/shared-components/help-overlay-button/help-overlay-button-wizard";
import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import Logo from "@/components/molecules/logo-component/logo";
import QuoteCover from "@/components/organisms/renewals/cover-level/cover-level-content";
import withQuoteData from "@/components/with-quote-data";
import { RenewalCoverLevel } from "@/config/app-constant";
import useGtmPageView from "@/hooks/gtm/use-gtm-page-view";
import useCustomPageTracking from "@/hooks/page-tracking/use-custom-page-tracking";
import useMediaQuery from "@/hooks/use-media-query/use-media-query-enhanced";
import {
  CoverAreaDataTypeRenewal,
  CoverContentTypeRenewal,
  CoverDrawerDetailsDataTypeRenewal,
} from "@/schema/renewal-page/cover-level-page.schema";
import { RootState } from "@/store/root-reducer";
import getFullPageRelativeTop from "@/utils/get-related-page-top";

interface RenewalCurrentPolicyWrapperProps {
  headerContent: ReactElement;
  acoSectionContent?: ReactElement;
  quoteCoverContent: CoverContentTypeRenewal | null;
  coverLevelArea?: CoverAreaDataTypeRenewal;
  drawerData?: CoverDrawerDetailsDataTypeRenewal | null;
  footerContinueCta: string;
}

function RenewalCoverLevelWrapper({
  headerContent,
  acoSectionContent,
  quoteCoverContent,
  coverLevelArea,
  drawerData,
  footerContinueCta,
}: Readonly<RenewalCurrentPolicyWrapperProps>) {
  const router = useRouter();
  const { current } = useWizard();
  const [isdesktop, isReady] = useMediaQuery("(min-width: 600px)");
  const [scrolled, setScrolled] = React.useState(false);
  const [isOpenUnsavedChangesModal, setIsOpenUnsavedChangesModal] =
    useState(false);

  const beforeAcoRef = useRef<HTMLDivElement>(null);

  const { pageReferrer, pageUrl, pageTitle } =
    useCustomPageTracking(RenewalCoverLevel);

  useGtmPageView({
    userId: "",
    quoteId: "",
    pageReferrer,
    pageUrl,
    pageTitle,
  });

  const { referenceId, attributes } = useSelector(
    (state: RootState) => state.quote
  );
  const { acoSection, tripDetails } = attributes;
  const isPreviousCoverEssentials = tripDetails?.isPreviousCoverEssentials;

  const _handleClickBack = () => {
    if (isPreviousCoverEssentials) {
      setIsOpenUnsavedChangesModal(true);
    } else {
      router.push(current.prev ?? "");
    }
  };

  const handleClickContinue = () => router.push(current.next ?? "");

  useEffect(() => {
    if (!scrolled) {
      // need to scroll top on page load
      window.scrollTo({ top: -100, behavior: "smooth" });
    }
    if (scrolled) {
      setTimeout(function f() {
        const topOffset = getFullPageRelativeTop(
          beforeAcoRef?.current as HTMLElement
        );

        const elementHeight = beforeAcoRef?.current?.offsetHeight as number;

        window.scrollTo({ top: topOffset + elementHeight, behavior: "smooth" });
      }, 200);

      setScrolled(false);
    }
  }, [scrolled]);

  if (!referenceId) return null;

  if (!isReady) return null;

  return (
    <div
      data-testid="renewals-cover-levels-test-id"
      className="bg-surface-neutral-option-2 "
      id="renewals-cover-levels"
    >
      <div className="max-w-[1200px] mx-auto my-0 w-[100%] relative min-h-[calc(100vh-12px)] lg:pb-0 pb-spacing-5xl ">
        <header className="md:pt-spacing-xl lg:py-spacing-xxl md:pb-spacing-4xl lg:pb-spacing-xxl pt-spacing-l pb-spacing-l">
          <div
            className=" md:gap-spacing-l gap-spacing-m xl:mx-auto lg:mx-[72px] 
      md:mx-[48px] mx-[24px] relative"
          >
            <div className="bg-dark-surface-accent-1-primary rounded-radius-xxl">
              <div className="flex flex-col h-full ">
                <div className="text-surface-neutral-option-1 relative">
                  <HelpOverlayButton />
                  <Logo
                    color="white"
                    heightClass="h-[80px]"
                    size="xl"
                    showondesktop={isdesktop.toString()}
                  />
                </div>
                {headerContent}
              </div>
            </div>
          </div>
        </header>

        <main className="xl:mx-auto lg:mx-[72px] mb-spacing-l lg:mb-0">
          <div
            className="grid grid-cols-6 md:grid-cols-12  lg:bg-surface-neutral-option-1 rounded-2xl"
            ref={beforeAcoRef}
          >
            <div className="col-span-12">
              <QuoteCover
                quoteCoverContent={quoteCoverContent}
                coverLevelArea={coverLevelArea}
                drawerData={drawerData}
                setScrolled={setScrolled}
                isUnsavedModalOpen={isOpenUnsavedChangesModal}
                setIsUnsavedModalOpen={setIsOpenUnsavedChangesModal}
              />
            </div>
          </div>

          {acoSection?.isACOSectionVisible && (
            <div className="grid grid-cols-6 md:grid-cols-12  lg:bg-surface-neutral-option-1 rounded-2xl mt-spacing-l md:mt-spacing-xl lg:mt-spacing-xxl">
              <div className="col-span-12">{acoSectionContent}</div>
            </div>
          )}
        </main>

        <footer>
          <FooterRenewalCoverLevel
            footerContinueCta={footerContinueCta}
            disableMain={tripDetails?.initialQuotePageLoading}
            onClickContinue={handleClickContinue}
          />
        </footer>
      </div>
    </div>
  );
}

export default withQuoteData(RenewalCoverLevelWrapper);
